import type { PermissionDefinition } from './permission.types';

/**
 * Registrul canonic de permisiuni.
 *
 * **Fiecare cheie de aici corespunde unui tabel care există deja.** Nu se adaugă chei
 * pentru ecrane neconstruite: o permisiune care nu păzește nimic arată, în matricea de
 * roluri, exact ca una care păzește ceva — și cine o bifează crede că a acordat un drept.
 * Cheile pentru documente au intrat odată cu `crm_documents` (#15); cele pentru semnare,
 * odată cu `crm_signing_sessions` (#18).
 *
 * Perechea `view` / `manage` e deliberat grosieră. Granularitatea (`create` separat de
 * `delete`) se adaugă când cineva chiar cere un rol care are unul fără celălalt;
 * până atunci ar fi douăzeci de bife pe care nimeni nu le poate combina util.
 *
 * Vezi docs/wiki/architecture/auth.md
 */
export const permissionRegistry = [
  {
    key: 'companies.view',
    area: 'companies',
    note: 'Vede firmele grupului și le poate alege ca firmă activă.',
  },
  {
    key: 'companies.manage',
    area: 'companies',
    note: 'Adaugă și modifică firme ale grupului (`crm_companies`).',
  },

  {
    key: 'customers.view',
    area: 'customers',
    note: 'Vede fișele de client.',
  },
  {
    key: 'customers.manage',
    area: 'customers',
    note: 'Adaugă și modifică fișele de client (`crm_customers`).',
  },

  {
    key: 'partners.view',
    area: 'partners',
    note: 'Vede lista de parteneri a contabilității (`crm_partners`).',
  },
  {
    key: 'partners.manage',
    area: 'partners',
    note: 'Adaugă și modifică parteneri. Codul fiscal al unui partener persoană fizică e un CNP.',
  },

  {
    key: 'catalog.view',
    area: 'catalog',
    note: 'Vede catalogul: pachete, tipuri de pachet, profile de client, tipuri de document.',
  },
  {
    key: 'catalog.manage',
    area: 'catalog',
    note: 'Modifică catalogul. Prețul implicit al unui pachet se schimbă de aici.',
  },

  {
    key: 'templates.view',
    area: 'templates',
    note: 'Vede template-urile de document și conținutul lor (`tpl_templates`).',
  },
  {
    key: 'templates.manage',
    area: 'templates',
    note: 'Creează, editează, publică și șterge template-uri de document.',
  },

  {
    key: 'templates.sources',
    area: 'templates',
    note: 'Declară surse de date pentru listele din template-uri (`tpl_data_sources`). Poate expune, în principiu, orice coloană din bază.',
  },

  {
    key: 'subscriptions.view',
    area: 'subscriptions',
    note: 'Vede abonamentele clientului și istoricul lor de stări.',
  },
  {
    key: 'subscriptions.manage',
    area: 'subscriptions',
    note: 'Adaugă și modifică abonamente, le activează, suspendă și reziliază (`crm_subscriptions`).',
  },

  {
    key: 'documents.view',
    area: 'documents',
    note: 'Vede registrul de documente al clientului: numere, stări, motive de anulare.',
  },
  {
    key: 'documents.manage',
    area: 'documents',
    // ⚠️ **Acoperă și ștergerea unei ciorne** (#142), fără cheie nouă. Registrul e deliberat
    // grosier — vezi nota de sus — iar aruncarea unui act **neemis** e mai blândă decât
    // anularea unuia emis, pe care cheia asta o permite deja. O cheie separată ar fi cerut un
    // rol în plus de administrat pentru operația mai puțin gravă dintre cele două. Se adaugă
    // când cineva chiar cere un rol care poate anula fără să poată șterge.
    note: 'Generează ciorne de document, le trimite la semnat, le anulează cu motiv și șterge ciornele la care se renunță. Numărul din registru se alocă la semnare.',
  },

  {
    key: 'signing.view',
    area: 'signing',
    note: 'Vede sesiunile de semnare ale clientului: ce a plecat, ce s-a deschis, ce s-a semnat.',
  },
  {
    key: 'signing.manage',
    area: 'signing',
    note: 'Trimite documente la semnat (`crm_signing_sessions`) și invalidează sesiuni. Invalidarea deblochează documentele.',
  },

  // Au intrat odată cu `inv_invoices` (#184). Emiterea e cheie separată, nu `manage`: o
  // factură emisă nu se mai desface (storno e G8), deci cine vede lista lunii nu primește
  // din oficiu și butonul care consumă numere.
  {
    key: 'invoices.view',
    area: 'invoices',
    note: 'Vede Conta → Facturare și tabul Financiar din fișa clientului: clienții lunii, facturile emise, PDF-ul și XML-ul lor.',
  },
  {
    key: 'invoices.issue',
    area: 'invoices',
    note: 'Pune facturi în lot și emite din fișa clientului (cu număr din seria firmei), și șterge probele. Facturile reale nu se șterg pe niciun drum.',
  },

  {
    key: 'settings.view',
    area: 'settings',
    note: 'Vede setările aplicației (`crm_settings`).',
  },
  {
    key: 'settings.manage',
    area: 'settings',
    note: 'Modifică setările aplicației.',
  },

  // Fără `activity.manage`, deliberat: jurnalul e append-only, deci nu există nimic de
  // administrat pe el. O cheie care nu păzește nicio acțiune arată în matricea de roluri
  // exact ca una care păzește ceva.
  {
    key: 'activity.view',
    area: 'activity',
    note: 'Vede jurnalul de activitate (`crm_activity_log`): cine ce a modificat și când.',
  },

  {
    key: 'users.view',
    area: 'users',
    note: 'Vede lista de operatori și sesiunile lor active.',
  },
  {
    key: 'users.manage',
    area: 'users',
    note: 'Creează operatori, le schimbă parola, îi dezactivează, le închide sesiunile.',
  },

  {
    key: 'roles.view',
    area: 'roles',
    note: 'Vede rolurile și matricea lor de permisiuni.',
  },
  {
    key: 'roles.manage',
    area: 'roles',
    note: 'Creează și modifică roluri, le atribuie operatorilor. Permisiunea care le dă pe toate celelalte.',
  },
] as const satisfies readonly PermissionDefinition[];

/**
 * Uniunea tuturor cheilor. Derivată din registru, nu scrisă de mână — de asta o cheie
 * inexistentă e o eroare de compilare, nu o verificare care trece tăcut.
 */
export type PermissionKey = (typeof permissionRegistry)[number]['key'];

/** Toate cheile, în ordinea din registru. */
export const permissionKeys: readonly PermissionKey[] = permissionRegistry.map(
  (permission) => permission.key,
);

const permissionKeySet: ReadonlySet<string> = new Set<string>(permissionKeys);

export function isPermissionKey(value: string): value is PermissionKey {
  return permissionKeySet.has(value);
}

/**
 * Păstrează doar cheile pe care registrul le cunoaște.
 *
 * `crm_roles.permissions` e JSON scris cândva în trecut; o cheie scoasă din registru la
 * un refactor rămâne acolo. Se **ignoră**, nu se aruncă eroare: altfel un rol vechi ar
 * bloca autentificarea tuturor celor care îl au, iar redenumirea unei chei ar cere
 * migrare de date.
 */
export function filterKnownPermissionKeys(values: readonly string[]): PermissionKey[] {
  return values.filter(isPermissionKey);
}
